// Centralized voucher-related helpers

/**
 * Get user's voucher balance.
 * Returns a number (NaN-safe: returns null when parsing fails).
 */
export async function getVoucherBalance({ userId, token }) {
  if (!userId) return null;
  const url = `https://boxmealadminapinew.azurewebsites.net/api/Fridge/get-voucher-balance?userId=${encodeURIComponent(userId)}`;
  const res = await fetch(url, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "Content-Type": "application/json",
    },
  });

  // If not OK, try to read text for diagnostics and return null
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    console.warn("[getVoucherBalance] Non-OK response:", res.status, t);
    return null;
  }

  try {
    const data = await res.clone().json();
    // console.log("Voucher balance data:", data);
    if (
      typeof data === "object" &&
      data !== null &&
      Object.prototype.hasOwnProperty.call(data, "balance")
    ) {
      const n = Number(data.balance);
      return Number.isFinite(n) ? n : null;
    }
    const n = Number(data);
    return Number.isFinite(n) ? n : null;
  } catch (_) {
    const text = await res.text();
    const n = Number(text);
    return Number.isFinite(n) ? n : null;
  }
}

/**
 * Perform a voucher balance purchase.
 * Input: { selectedFridgeId, cartItems, token, voucherBalance }
 * Output: { ok: boolean, error?: string, unlockCode?: string, result?: any }
 */
export async function handleVoucherBalancePay({ selectedFridgeId, cartItems, token, voucherBalance }) {
  try {
    const total = Number(
      (cartItems || []).reduce((acc, item) => acc + Number(item.price) * Number(item.quantity), 0).toFixed(2)
    );

    if (voucherBalance == null || total > Number(voucherBalance)) {
      return { ok: false, error: "Du har inte tillräckligt med saldo." };
    }

    const payload = {
      FridgeId: Number(selectedFridgeId),
      TotalAmount: total,
      Items: (cartItems || []).map((item) => ({
        ProductId: Number(item.id),
        Quantity: Number(item.quantity),
        Price: Number(item.price),
      })),
    };

    const url = `https://boxmealadminapinew.azurewebsites.net/api/Fridge/purchase-with-voucher`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      return { ok: false, error: text || "Köpet misslyckades.", status: response.status };
    }

    const result = await response.json().catch(() => ({}));
    const unlockCode = result?.unlockCode || result?.qrCode || "";
    return { ok: true, unlockCode, result };
  } catch (e) {
    return { ok: false, error: "Något gick fel vid betalning.", exception: String(e && e.message ? e.message : e) };
  }
}

/**
 * Credit user's voucher balance from a PAID Swish payment.
 * The backend should derive the amount from the paymentRequestId to ensure integrity.
 * Input: { paymentRequestId, userId, token }
 * Output: { ok: boolean, data?: any, error?: string, status?: number }
 */
export async function creditVoucherBalance({ paymentRequestId, userId, token }) {
  try {
    if (!paymentRequestId || !userId) {
      return { ok: false, error: "Missing paymentRequestId or userId" };
    }
    const res = await fetch("/api/Fridge/CreditVoucherBalance", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ paymentRequestId, userId }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      return { ok: false, status: res.status, error: text || "Credit failed" };
    }
    const data = await res.json().catch(() => ({}));
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: String(e && e.message ? e.message : e) };
  }
}
